/**
 * WelcomeTemplateSetting — lets the user personalise the greeting
 * that auto-fills into a new DM when they click "Chat" on a fresh
 * invite redemption.
 *
 * Stored in localStorage under `ghostcomm_welcome_template` so it
 * lives on-device only (no server round-trip, no metadata leak)
 * and is wiped by the panic button along with everything else
 * under the `ghostcomm_*` prefix.
 *
 * Placeholders: `{name}` (raw recipient name) and `{who}` (", Alex"
 * when the name is known; blank otherwise — lets the built-in
 * "Hey{who}" copy read naturally either way).
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MessageSquarePlus, RotateCcw, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';

const KEY = 'ghostcomm_welcome_template';
const BUILTIN =
  'Hey{who} — glad you made it onto Government Shield.\n\n' +
  'This chat is end-to-end encrypted; use it as the default way to ' +
  'reach me from now on. 🛡';

const _preview = (tpl, name = 'Alex') =>
  tpl.replaceAll('{name}', name).replaceAll('{who}', name ? `, ${name}` : '');

export const WelcomeTemplateSetting = () => {
  const [draft, setDraft] = useState('');
  const [saved, setSaved] = useState('');

  useEffect(() => {
    try {
      const v = localStorage.getItem(KEY) || '';
      setDraft(v);
      setSaved(v);
    } catch {
      /* private-mode localStorage — fall back to builtin */
    }
  }, []);

  const dirty = draft !== saved;
  const effective = draft && draft.trim() ? draft : BUILTIN;

  const save = () => {
    try {
      if (draft.trim()) {
        localStorage.setItem(KEY, draft);
      } else {
        localStorage.removeItem(KEY);
      }
      setSaved(draft);
      toast.success('Welcome message saved');
    } catch {
      toast.error('Unable to save — storage locked');
    }
  };

  const reset = () => {
    setDraft('');
    try {
      localStorage.removeItem(KEY);
    } catch { /* noop */ }
    setSaved('');
    toast.success('Reverted to the default greeting');
  };

  return (
    <div className="space-y-2" data-testid="welcome-template-setting">
      <label className="text-sm text-slate-400 flex items-center gap-2">
        <MessageSquarePlus className="w-4 h-4" />
        Welcome message
      </label>
      <div className="p-4 rounded-lg bg-secondary/30 space-y-3">
        <p className="text-xs text-slate-500">
          Pre-fills the chat compose buffer the first time you say hi to
          someone who just joined through your invite. Edit once; every
          new greeting uses your wording. Stored on-device only; the
          panic button wipes it.
        </p>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={4}
          maxLength={500}
          placeholder={BUILTIN}
          className="bg-secondary/40 border-border text-white font-normal resize-none text-sm"
          data-testid="welcome-template-textarea"
        />
        <div className="flex items-center justify-between text-[11px] text-slate-500">
          <span>
            Placeholders: <code className="text-slate-300">{'{name}'}</code>{' '}
            <code className="text-slate-300">{'{who}'}</code>{' '}
            (auto-adds a comma)
          </span>
          <span>{draft.length} / 500</span>
        </div>

        <div className="rounded-md bg-background/40 border border-border/60 p-3 text-xs text-slate-300 whitespace-pre-wrap">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
            Preview (for Alex)
          </div>
          {_preview(effective, 'Alex')}
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            onClick={save}
            disabled={!dirty}
            className="bg-primary hover:bg-primary/90"
            data-testid="welcome-template-save"
          >
            <Save className="w-3.5 h-3.5 mr-1" /> Save
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={reset}
            disabled={!saved && !draft}
            data-testid="welcome-template-reset"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to default
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeTemplateSetting;
